import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore.ts';
import { Button } from '../../components/Shared/Button.tsx';
import { FormField } from '../../components/Shared/FormField.tsx';
import { useTranslation } from '../../i18n';
import { AuthLayout } from './AuthLayout.tsx';

export function ForgotPassword() {
  const { t } = useTranslation(['auth', 'common']);
  const { forgotPassword } = useAuthStore();
  const [personalNumber, setPersonalNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await forgotPassword(personalNumber, email || undefined);
      setSubmitted(true);
    } catch {
      setError(t('auth:forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
            {t('auth:forgotPassword.kicker')}
          </div>
          <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mt-0.5">
            {t('auth:forgotPassword.title')}
          </h2>
        </div>

        {submitted ? (
          <div className="p-5 space-y-4">
            <div
              role="status"
              className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
            >
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-[12px] text-emerald-800">{t('auth:forgotPassword.success')}</p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#00A871] hover:underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
              {t('auth:forgotPassword.backToLogin')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <p className="text-[12px] text-slate-500">{t('auth:forgotPassword.description')}</p>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
              >
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-[12px] text-red-700">{error}</p>
              </div>
            )}

            <FormField
              label={t('auth:forgotPassword.personalNumber')}
              id="personalNumber"
              type="text"
              value={personalNumber}
              onChange={(e) => setPersonalNumber(e.target.value)}
              placeholder={t('auth:login.placeholders.personalNumber')}
              required
              autoComplete="username"
              autoFocus
              className="font-mono-num"
            />

            <FormField
              label={t('auth:forgotPassword.email')}
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth:forgotPassword.emailPlaceholder')}
              autoComplete="email"
              helperText={t('auth:forgotPassword.emailHint')}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full justify-center mt-2"
              disabled={isLoading}
              leftIcon={
                isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" strokeWidth={2} />
                )
              }
            >
              {isLoading ? t('auth:forgotPassword.submitting') : t('auth:forgotPassword.submit')}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
                {t('auth:forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
